import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'existingSection',
    'newSection',
    'blankSection',
    'existingSelect',
    'existingHiddenInput',
    'newUserField',
    'blankUserField',
    'existingModeButton',
    'newModeButton',
    'blankModeButton'
  ]

  static values = {
    defaultMode: { type: String, default: 'new' },
    allowBlankUser: { type: Boolean, default: false }
  }

  connect () {
    this.initialExistingUserId = this.selectedExistingUserId()
    const mode = ['existing', 'blank'].includes(this.defaultModeValue) ? this.defaultModeValue : 'new'
    this.show(mode)
  }

  showExisting () {
    this.show('existing')
  }

  showNew () {
    this.show('new')
  }

  showBlank () {
    this.show('blank')
  }

  // From the duplicate notice: switches to the existing account and picks it.
  useExisting ({ params: { userId, userLabel } }) {
    this.show('existing')
    if (!this.hasExistingSelectTarget) return

    const id = String(userId)
    const tomSelect = this.existingSelectTarget.tomselect
    if (tomSelect) {
      tomSelect.addOption({ id, text: userLabel })
      tomSelect.setValue(id)
      return
    }

    this.existingSelectTarget.add(new Option(userLabel, id, true, true))
  }

  syncExistingSelect () {
    if (!this.currentMode) return

    this.syncExistingUserFields(this.currentMode === 'existing')
  }

  show (mode) {
    this.currentMode = mode
    const isExisting = mode === 'existing'
    const isBlank = mode === 'blank'

    if (this.hasExistingSectionTarget) {
      this.existingSectionTarget.classList.toggle('d-none', !isExisting)
    }

    if (this.hasNewSectionTarget) {
      this.newSectionTarget.classList.toggle('d-none', !(!isExisting && !isBlank))
    }

    if (this.hasBlankSectionTarget) {
      this.blankSectionTarget.classList.toggle('d-none', !isBlank)
    }

    this.newUserFieldTargets.forEach((field) => {
      field.disabled = isExisting || isBlank
    })

    this.blankUserFieldTargets.forEach((field) => {
      field.disabled = !isBlank
    })

    this.syncExistingUserFields(isExisting)
    this.updateButtons(mode)
  }

  syncExistingUserFields (isExisting) {
    if (this.hasExistingHiddenInputTarget) {
      this.existingHiddenInputTarget.disabled = isExisting
      if (!isExisting) this.existingHiddenInputTarget.value = ''
    }

    if (this.hasExistingSelectTarget) {
      const tomSelect = this.existingSelectTarget.tomselect

      this.existingSelectTarget.disabled = !isExisting

      if (tomSelect) {
        if (isExisting) {
          tomSelect.enable()
          this.restoreExistingUser(tomSelect)
        } else {
          tomSelect.clear(true)
          tomSelect.disable()
        }
      } else if (isExisting) {
        this.restoreExistingUser()
      }
    }
  }

  selectedExistingUserId () {
    if (!this.hasExistingSelectTarget) return ""

    return this.existingSelectTarget.querySelector("option[selected]")?.value || ""
  }

  restoreExistingUser (tomSelect = null) {
    if (!this.initialExistingUserId || !this.hasExistingSelectTarget) return
    if (this.existingSelectTarget.value) return

    this.existingSelectTarget.value = this.initialExistingUserId
    if (tomSelect && !tomSelect.getValue()) tomSelect.setValue(this.initialExistingUserId, true)
  }

  updateButtons (mode) {
    this.setModeButton(this.existingModeButtonTarget, mode === 'existing')
    this.setModeButton(this.newModeButtonTarget, mode === 'new')

    if (this.hasBlankModeButtonTarget) {
      this.setModeButton(this.blankModeButtonTarget, mode === 'blank')
    }
  }

  setModeButton (button, active) {
    if (button.classList.contains('user-mode-switch__button')) {
      button.classList.toggle('is-active', active)
      return
    }

    button.classList.toggle('btn-gray', active)
    button.classList.toggle('btn-default', !active)
  }
}
